import { useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { BrandMark } from "@/components/Brand";
import FrostReveal from "@/components/FrostReveal";
import { REVEAL_MODELS } from "@/constants/reveal";
import { Venus, Mars, Loader2, Hand, Sparkles } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  // Always ask orientation on each visit to the gateway (choice still saved for Blind Date).
  const [orientation, setOrientation] = useState("");
  const [revealed, setRevealed] = useState(false);
  const [progress, setProgress] = useState(0);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const pick = (o) => { setOrientation(o); localStorage.setItem("ah_orientation", o); };

  const onProgress = useCallback((p) => {
    setProgress(p);
    if (p >= 0.82) setRevealed(true);
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) {
      toast.success("Welcome back");
      if (res.user.role === "admin") nav("/admin");
      else if (res.user.preferred_panel === "blinddate") nav("/blinddate");
      else if (res.user.preferred_panel === "companion") nav("/app");
      else nav("/portal");
    }
    else toast.error(res.error);
  };

  const models = REVEAL_MODELS[orientation === "male" ? "male" : "female"];

  return (
    <div className="h-[100dvh] w-screen relative bg-ink overflow-hidden">
      {/* Phase 2+3: models behind a frost canvas the user scratches to reveal */}
      {orientation && <FrostReveal images={models} onProgress={onProgress} />}

      {/* Phase 1: orientation matrix modal */}
      <Dialog open={!orientation}>
        <DialogContent className="bg-surface border-white/10 rounded-3xl max-w-md" data-testid="orientation-modal">
          <DialogTitle className="font-heading text-2xl font-bold text-center">Identity Matrix Check</DialogTitle>
          <DialogDescription className="text-center text-muted-foreground">
            Select your primary orientation trajectory
          </DialogDescription>
          <div className="grid grid-cols-2 gap-4 mt-4">
            <button data-testid="orientation-female" onClick={() => pick("female")}
              className="p-6 rounded-2xl border border-white/10 bg-ink hover:border-brand hover:bg-brand/10 transition-colors flex flex-col items-center gap-3">
              <div className="w-14 h-14 rounded-2xl bg-brand/20 flex items-center justify-center"><Venus className="w-7 h-7 text-brand" /></div>
              <span className="font-medium text-sm text-center">Attracted to Female Profiles</span>
            </button>
            <button data-testid="orientation-male" onClick={() => pick("male")}
              className="p-6 rounded-2xl border border-white/10 bg-ink hover:border-brand hover:bg-brand/10 transition-colors flex flex-col items-center gap-3">
              <div className="w-14 h-14 rounded-2xl bg-brand/20 flex items-center justify-center"><Mars className="w-7 h-7 text-brand" /></div>
              <span className="font-medium text-sm text-center">Attracted to Male Profiles</span>
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Scratch hint — visible before the panel appears */}
      {orientation && !revealed && (
        <div className="absolute inset-0 z-20 flex flex-col items-center justify-center pointer-events-none px-6 text-center animate-fade-in">
          <div className="glass border border-white/10 rounded-full px-5 py-3 flex items-center gap-2 brand-glow">
            <Hand className="w-5 h-5 text-brand animate-pulse" />
            <span className="text-sm font-medium">Swipe across the screen to reveal</span>
          </div>
          <div className="mt-5 w-48 h-1.5 rounded-full bg-white/10 overflow-hidden">
            <div className="h-full bg-brand transition-[width] duration-200" style={{ width: `${Math.min(100, progress * 100)}%` }} />
          </div>
          <p className="mt-2 text-xs text-muted-foreground">{Math.round(Math.min(100, progress * 100))}% revealed</p>
        </div>
      )}

      {/* Phase: login panel fades in slowly between the models */}
      {orientation && revealed && (
        <div className="absolute inset-0 z-30 flex flex-col items-center justify-center p-6"
          style={{ animation: "brand-fade 1.1s ease-out both" }}>
          <div className="mb-6"><BrandMark className="text-3xl sm:text-4xl" /></div>
          <div className="glass border border-white/10 rounded-3xl p-6 sm:p-7 w-full max-w-sm brand-glow">
            <form onSubmit={submit} className="space-y-3">
              <Input data-testid="login-email-input" type="email" required placeholder="Email"
                value={email} onChange={(e) => setEmail(e.target.value)}
                className="bg-ink/80 border-white/10 rounded-2xl h-14" />
              <Input data-testid="login-password-input" type="password" required placeholder="Password"
                value={password} onChange={(e) => setPassword(e.target.value)}
                className="bg-ink/80 border-white/10 rounded-2xl h-14" />
              <Button data-testid="login-submit-button" disabled={loading}
                className="w-full h-14 rounded-2xl bg-brand hover:bg-brand-hover text-white font-semibold transition-colors brand-glow">
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Enter"}
              </Button>
            </form>
            <p className="text-sm text-muted-foreground mt-5 text-center">
              New here? <Link to="/register" data-testid="go-register-link" className="text-brand font-semibold">Create an account</Link>
            </p>
          </div>
          <div className="mt-6 flex gap-4 text-xs text-muted-foreground">
            <Link to="/privacy" className="hover:text-white">Privacy</Link>
            <Link to="/terms" className="hover:text-white">Terms</Link>
          </div>
        </div>
      )}
    </div>
  );
}
